// Sign-in against the server's own OAuth server, the same one Claude uses: the app registers itself at /register
// as a public client (PKCE, no secret) with the redirect URI <origin>/auth, signs in through Entra and keeps the
// access token (60 min) and the rotating refresh token (90 days) in localStorage.

export const API_BASE = (import.meta.env.VITE_API_BASE ?? "https://boelit-yahoomail-mcp.azurewebsites.net").replace(/\/$/, "");

const TOKENS_KEY = `ym.tokens:${API_BASE}`;
const PENDING_KEY = `ym.pending:${API_BASE}`;
const SCOPE = "mail";
const REFRESH_MARGIN_MS = 60_000;

export interface Tokens {
  access_token: string;
  refresh_token: string | null;
  expires_at: number; // epoch ms
  client_id: string;
}

interface Pending {
  state: string;
  verifier: string;
  client_id: string;
  redirect_uri: string;
  return_to: string;
  created_at: number;
}

/** The refresh token was refused (expired, revoked or used twice): the user must sign in again. */
export class SignedOut extends Error {
  constructor(message = "Signed out") {
    super(message);
    this.name = "SignedOut";
  }
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  if (value === null) localStorage.removeItem(key);
  else localStorage.setItem(key, JSON.stringify(value));
}

export function storedTokens(): Tokens | null {
  return read<Tokens>(TOKENS_KEY);
}

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(bytes = 32): string {
  return base64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

async function challengeFor(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(new Uint8Array(digest));
}

function redirectUri(): string {
  return `${location.origin}/auth`;
}

async function oauthError(res: Response): Promise<string> {
  try {
    const body = await res.json();
    return [body.error, body.error_description].filter(Boolean).join(": ") || `HTTP ${res.status}`;
  } catch {
    return `HTTP ${res.status}`;
  }
}

/** Registers a fresh public client (sign-in happens every few months at most, so a client per sign-in is cheap and
 * never stale) and sends the browser to the server's /authorize, which goes on to Entra. */
export async function startSignIn(returnTo: string): Promise<never> {
  const redirect = redirectUri();
  const reg = await fetch(`${API_BASE}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_name: `Yahoo Mail web app (${location.host})`,
      redirect_uris: [redirect],
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
      token_endpoint_auth_method: "none",
      scope: SCOPE,
    }),
  });
  if (!reg.ok) throw new Error(`Could not register the app with the server: ${await oauthError(reg)}`);
  const { client_id } = (await reg.json()) as { client_id: string };

  const verifier = randomString(48);
  const state = randomString(24);
  const pending: Pending = { state, verifier, client_id, redirect_uri: redirect, return_to: returnTo, created_at: Date.now() };
  write(PENDING_KEY, pending);

  const url = new URL(`${API_BASE}/authorize`);
  url.search = new URLSearchParams({
    response_type: "code",
    client_id,
    redirect_uri: redirect,
    code_challenge: await challengeFor(verifier),
    code_challenge_method: "S256",
    state,
    scope: SCOPE,
  }).toString();
  location.assign(url.toString());
  return new Promise<never>(() => {});
}

/** On /auth?code=...&state=...: exchanges the code for tokens and returns where to go next. */
export async function completeSignIn(params: URLSearchParams): Promise<string> {
  const pending = read<Pending>(PENDING_KEY);
  write(PENDING_KEY, null);
  const error = params.get("error");
  if (error) throw new Error(`Sign-in failed: ${[error, params.get("error_description")].filter(Boolean).join(": ")}`);
  const code = params.get("code");
  if (!code || !pending || params.get("state") !== pending.state) {
    throw new Error("Sign-in could not be completed (it was started in another window or has expired). Try again.");
  }
  const res = await fetch(`${API_BASE}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: pending.redirect_uri,
      client_id: pending.client_id,
      code_verifier: pending.verifier,
    }),
  });
  if (!res.ok) throw new Error(`Sign-in failed: ${await oauthError(res)}`);
  saveTokens(await res.json(), pending.client_id);
  return pending.return_to || "/";
}

function saveTokens(body: { access_token: string; refresh_token?: string; expires_in?: number }, clientId: string): Tokens {
  const tokens: Tokens = {
    access_token: body.access_token,
    refresh_token: body.refresh_token ?? null,
    expires_at: Date.now() + (body.expires_in ?? 3600) * 1000,
    client_id: clientId,
  };
  write(TOKENS_KEY, tokens);
  return tokens;
}

let refreshing: Promise<Tokens> | null = null;

/** Refresh tokens rotate: the old one is revoked when used, so two tabs refreshing at once would sign one out.
 * One refresh at a time per browser (Web Locks), and a tab that waited uses the token the other one got. */
function refresh(stale: Tokens): Promise<Tokens> {
  if (refreshing) return refreshing;
  const run = async (): Promise<Tokens> => {
    const current = storedTokens();
    if (!current) throw new SignedOut();
    if (current.access_token !== stale.access_token && current.expires_at - REFRESH_MARGIN_MS > Date.now()) return current;
    if (!current.refresh_token) throw new SignedOut("The session has expired");
    const res = await fetch(`${API_BASE}/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ grant_type: "refresh_token", refresh_token: current.refresh_token, client_id: current.client_id }),
    });
    if (res.status === 400 || res.status === 401) {
      write(TOKENS_KEY, null);
      throw new SignedOut(`The session has ended (${await oauthError(res)})`);
    }
    if (!res.ok) throw new Error(`Could not renew the session: ${await oauthError(res)}`);
    return saveTokens(await res.json(), current.client_id);
  };
  const locked = navigator.locks ? (navigator.locks.request(`ym-refresh:${API_BASE}`, run) as unknown as Promise<Tokens>) : run();
  const pending = locked.finally(() => {
    refreshing = null;
  });
  refreshing = pending;
  return pending;
}

/** A valid access token, renewed when it is about to expire; null when the app has never signed in. */
export async function accessToken(forceRefresh = false): Promise<string | null> {
  const tokens = storedTokens();
  if (!tokens) return null;
  if (!forceRefresh && tokens.expires_at - REFRESH_MARGIN_MS > Date.now()) return tokens.access_token;
  return (await refresh(tokens)).access_token;
}

export async function signOut(): Promise<void> {
  const tokens = storedTokens();
  write(TOKENS_KEY, null);
  if (!tokens) return;
  for (const token of [tokens.refresh_token, tokens.access_token]) {
    if (!token) continue;
    try {
      await fetch(`${API_BASE}/revoke`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ token, client_id: tokens.client_id }),
      });
    } catch {
      // Offline or the server is down: the tokens are gone from this device, which is what matters.
    }
  }
}
